import { useCallback, useEffect, useState } from "react";
import type { CustomDateRange, RangeMode, TimeRange } from "./api";
import type { NavKey } from "./router";
import { buildUrl, parseUrl } from "./router";

export type UseTimeRangeOpts = {
  initial?: {
    timeRange?: TimeRange | null;
    rangeMode?: RangeMode | null;
    customRange?: CustomDateRange | null;
  };
  sync?: "callback" | "url" | "none";
  onRangeChange?: (
    timeRange: TimeRange,
    rangeMode: RangeMode,
    customRange?: CustomDateRange | null,
  ) => void;
  urlNavKey?: NavKey;
};

export type UseTimeRangeReturn = {
  timeRange: TimeRange;
  rangeMode: RangeMode;
  customRange: CustomDateRange | null;
  effectiveRangeMode: RangeMode;
  onTimeRangeChange: (next: TimeRange) => void;
  onRangeModeChange: (next: RangeMode) => void;
  onCustomRangeChange: (next: CustomDateRange) => void;
  onSwitchToAllTime: () => void;
};

function isModeless(tr: TimeRange): boolean {
  return tr === "daily" || tr === "all_time" || tr === "custom";
}

export function useTimeRange(opts: UseTimeRangeOpts = {}): UseTimeRangeReturn {
  const { sync = "none", onRangeChange, urlNavKey } = opts;
  const init = opts.initial;

  const [timeRange, setTimeRange] = useState<TimeRange>(
    init?.timeRange ?? "all_time",
  );
  const [rangeMode, setRangeMode] = useState<RangeMode>(
    init?.rangeMode ?? "rolling",
  );
  const [customRange, setCustomRange] = useState<CustomDateRange | null>(
    init?.customRange ?? null,
  );

  const effectiveRangeMode: RangeMode = isModeless(timeRange)
    ? "calendar"
    : rangeMode;

  const pushUrl = useCallback(
    (tr: TimeRange, rm: RangeMode, cr: CustomDateRange | null) => {
      if (sync !== "url" || !urlNavKey || typeof window === "undefined") return;
      const url = buildUrl(urlNavKey, null, null, {
        timeRange: tr,
        rangeMode: rm,
        customStart: cr?.start ?? null,
        customEnd: cr?.end ?? null,
      });
      if (window.location.pathname + window.location.search !== url) {
        window.history.pushState({}, "", url);
      }
    },
    [sync, urlNavKey],
  );

  const notify = useCallback(
    (tr: TimeRange, rm: RangeMode, cr: CustomDateRange | null) => {
      if (sync === "callback" && onRangeChange) {
        onRangeChange(tr, rm, cr);
      }
    },
    [sync, onRangeChange],
  );

  const onTimeRangeChange = useCallback(
    (next: TimeRange) => {
      setTimeRange(next);
      const nextCustom = next === "custom" ? customRange : null;
      if (next !== "custom") setCustomRange(null);
      pushUrl(next, rangeMode, nextCustom);
      notify(next, rangeMode, nextCustom);
    },
    [customRange, rangeMode, pushUrl, notify],
  );

  const onRangeModeChange = useCallback(
    (next: RangeMode) => {
      setRangeMode(next);
      pushUrl(timeRange, next, customRange);
      notify(timeRange, next, customRange);
    },
    [timeRange, customRange, pushUrl, notify],
  );

  const onCustomRangeChange = useCallback(
    (next: CustomDateRange) => {
      setCustomRange(next);
      const tr = timeRange !== "custom" ? "custom" as TimeRange : timeRange;
      if (timeRange !== "custom") setTimeRange(tr);
      pushUrl(tr, rangeMode, next);
      notify(tr, rangeMode, next);
    },
    [timeRange, rangeMode, pushUrl, notify],
  );

  const onSwitchToAllTime = useCallback(() => {
    setTimeRange("all_time");
    setRangeMode("rolling");
    setCustomRange(null);
    pushUrl("all_time", "rolling", null);
    notify("all_time", "rolling", null);
  }, [pushUrl, notify]);

  useEffect(() => {
    if (sync !== "url") return;
    const onPop = () => {
      const p = parseUrl();
      if (p.timeRange) setTimeRange(p.timeRange);
      if (p.rangeMode) setRangeMode(p.rangeMode);
      setCustomRange(
        p.customStart && p.customEnd
          ? { start: p.customStart, end: p.customEnd }
          : null,
      );
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [sync]);

  return {
    timeRange,
    rangeMode,
    customRange,
    effectiveRangeMode,
    onTimeRangeChange,
    onRangeModeChange,
    onCustomRangeChange,
    onSwitchToAllTime,
  };
}
